/**
 * Pure (no React / no store) helpers for the record-metadata editor's RecordTable
 * conversion (operator-task-fe-convert-independent-renderers-to-recordtable).
 *
 * The editor shows one row per `record_metadata` entry plus an optional pending
 * "new" row. Key and value are edited DIRECTLY in the table cells (no expand);
 * the per-row working edits are held in the parent component keyed by stable row
 * id, and these helpers derive the row baselines + dirty state from them. All of
 * this is unit-tested independently of the component.
 */

/** Stable synthetic row id for the pending (unsaved) new-metadata row. */
export const NEW_METADATA_ROW_ID = "__new_metadata_row__";

/** The editable value of one metadata row (the key + its serialized data). */
export interface MetadataEditValue {
    key: string;
    data: string;
}

export interface MetadataRow {
    /** Stable row key (the record_metadata id, or the synthetic new-row id). */
    id: string;
    /** True for the pending unsaved row (no persisted id yet). */
    isNew: boolean;
    /** The underlying metadata object — carries the persisted id for mutations. */
    metadata: any;
    /** Persisted key (the baseline the edited key is compared against). */
    keyText: string;
    /** Persisted data serialized to an editable string (the baseline). */
    dataText: string;
}

/**
 * Serialize a metadata `data` value to the string shown in the editable cell.
 * `data` may already be a string (as edited / stored) or a parsed object/scalar
 * returned by GraphQL. Objects are rendered as pretty JSON (valid YAML, so the
 * YAML validator accepts it and a round-trip save is faithful).
 */
export const metadataDataToString = (data: unknown): string => {
    if (data === null || data === undefined) return "";
    if (typeof data === "string") return data;
    try {
        return JSON.stringify(data, null, 2);
    } catch {
        return String(data);
    }
};

/**
 * Build the row model for the metadata editor's RecordTable: one row per saved
 * metadata entry (in the given order), plus the pending new row at the end when
 * present. Each row carries its persisted baseline (keyText/dataText); the live
 * edited value lives in the component's edits map keyed by row id.
 */
export const deriveMetadataRows = (
    metadataList: any[] | undefined | null,
    newRow: any | null | undefined
): MetadataRow[] => {
    const rows: MetadataRow[] = (metadataList ?? []).map((metadata: any) => ({
        id: String(metadata?.id),
        isNew: false,
        metadata,
        keyText: metadata?.key ?? "",
        dataText: metadataDataToString(metadata?.data),
    }));
    if (newRow) {
        rows.push({
            id: NEW_METADATA_ROW_ID,
            isNew: true,
            metadata: newRow,
            keyText: newRow?.key ?? "",
            dataText: metadataDataToString(newRow?.data),
        });
    }
    return rows;
};

/** The persisted baseline value a row's edits are compared against. */
export const baselineEditValue = (row: MetadataRow): MetadataEditValue => ({
    key: row.keyText,
    data: row.dataText,
});

/**
 * The live value shown in a row's cells: the working edit if the user has
 * touched the row, otherwise the persisted baseline.
 */
export const effectiveEditValue = (
    row: MetadataRow,
    edits: Record<string, MetadataEditValue>
): MetadataEditValue => edits[row.id] ?? baselineEditValue(row);

/**
 * Whether a row has unsaved changes: it has a working edit that differs from its
 * persisted baseline. A freshly-added (untouched) new row is NOT dirty — nothing
 * to lose yet — so adding a row and navigating away without typing does not trip
 * the unsaved-changes guard.
 */
export const isRowDirty = (
    row: MetadataRow,
    edits: Record<string, MetadataEditValue>
): boolean => {
    const eff = edits[row.id];
    if (!eff) return false;
    const base = baselineEditValue(row);
    return eff.key !== base.key || eff.data !== base.data;
};

/**
 * Whether a row's inline Confirm/Cancel actions should be shown: the pending new
 * row always shows them (so it can be confirmed or cancelled), and a saved row
 * shows them once it is dirty.
 */
export const showRowActions = (
    row: MetadataRow,
    edits: Record<string, MetadataEditValue>
): boolean => row.isNew || isRowDirty(row, edits);

/** True when any row in the list has unsaved changes (drives the nav guard). */
export const hasAnyUnsavedChange = (
    rows: MetadataRow[],
    edits: Record<string, MetadataEditValue>
): boolean => rows.some((row) => isRowDirty(row, edits));
